import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Scissors,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Image as ImageIcon,
  Edit3,
  Save,
  X,
} from 'lucide-react';
import { httpClient } from '../services/http';
import { clientAppointmentsApi } from '../services/clientAppointmentsApi';

interface AppointmentDetail {
  id: string;
  scheduledAt: string;
  estimatedEndTime: string;
  status: string;
  type: string;
  notes: string;
  clientNotes: string | null;
  shopName: string;
  shopAddress: Record<string, string>;
  shopPhone: string | null;
  serviceName: string;
  practitionerName: string;
  pricing: Record<string, any>;
  depositStatus: string | null;
  depositAmount: number | null;
  depositRequired: boolean;
  checkInCode?: string | null;
  project: {
    style: string;
    placement: string;
    size: string;
    description: string;
    colorType: string;
  } | null;
  referenceImages?: {
    id: string;
    url: string;
    thumbnailUrl: string | null;
    fileName: string;
    mimeType: string;
  }[];
}

const statusConfig: Record<string, { icon: typeof CheckCircle; color: string; bg: string }> = {
  CONFIRMED: { icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
  SCHEDULED: { icon: Calendar, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
  COMPLETED: { icon: CheckCircle, color: 'text-gray-700', bg: 'bg-gray-50 border-gray-200' },
  CANCELLED: { icon: XCircle, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  PENDING_PAYMENT: { icon: AlertCircle, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
};

export default function AppointmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [appointment, setAppointment] = useState<AppointmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await httpClient.get<{ success: boolean; data: AppointmentDetail }>(`/api/clients/appointments/${id}`);
        if (res.success) {
          setAppointment(res.data);
        } else {
          setError('Appointment not found');
        }
      } catch (err: any) {
        if (err.status === 404) {
          setError('Appointment not found');
        } else if (err.status !== 401) {
          // 401 is handled by the session-expired modal
          setError('Failed to load appointment');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary-500 mx-auto mb-4" />
        <p className="text-textSecondary">{t('auth.account.appointments.loading', 'Loading...')}</p>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-textPrimary mb-2">{error || 'Not found'}</h2>
        <Link to="/account/appointments" className="fo-link text-sm">
          <ArrowLeft className="w-4 h-4 inline mr-1" />
          {t('auth.account.appointments.title', 'My appointments')}
        </Link>
      </div>
    );
  }

  const scheduledAt = new Date(appointment.scheduledAt);
  const dateFormatted = scheduledAt.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timeFormatted = scheduledAt.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const addr = appointment.shopAddress;
  const shopAddressStr = [addr?.street, addr?.postalCode, addr?.city, addr?.country]
    .filter(Boolean)
    .join(', ');

  const statusKey = appointment.status?.toUpperCase() || 'SCHEDULED';
  const sc = statusConfig[statusKey] || statusConfig.SCHEDULED;
  const StatusIcon = sc.icon;

  const pricing = appointment.pricing || {};
  const totalPrice = pricing.totalPrice ?? pricing.basePrice ?? '—';
  const isConsultation = appointment.type === 'CONSULTATION';
  const isTattooSession = appointment.type === 'TATTOO_SESSION' || appointment.type === 'TATTOO';
  const hideTattooTotal = isConsultation || isTattooSession;

  // Extract plain-text client notes
  const clientNotesText = (() => {
    if (!appointment.clientNotes) return '';
    try {
      const parsed = JSON.parse(appointment.clientNotes);
      const html = parsed.noteHtml || '';
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    } catch {
      return appointment.clientNotes.replace(/<[^>]*>/g, '').trim();
    }
  })();

  const isEditable = ['SCHEDULED', 'CONFIRMED', 'PENDING_PAYMENT'].includes(statusKey);

  const depositStatusLabels: Record<string, string> = {
    PAID: t('appointmentDetail.depositPaid', 'Paid'),
    PENDING: t('appointmentDetail.depositPending', 'Pending'),
    NOT_REQUIRED: t('appointmentDetail.depositNotRequired', 'Not required'),
    FAILED: t('appointmentDetail.depositFailed', 'Failed'),
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Back link */}
      <Link
        to="/account/appointments"
        className="inline-flex items-center gap-1 text-sm text-textSecondary hover:text-primary-600 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('auth.account.appointments.title', 'My appointments')}
      </Link>

      {/* Status banner */}
      <div className={`flex items-center gap-3 px-5 py-4 rounded-xl border mb-6 ${sc.bg}`}>
        <StatusIcon className={`w-6 h-6 ${sc.color}`} />
        <div>
          <p className={`font-semibold ${sc.color}`}>
            {t(`auth.account.appointments.${statusKey.toLowerCase()}`, statusKey)}
          </p>
          <p className="text-sm text-textSecondary">{appointment.serviceName}</p>
        </div>
      </div>

      {/* Details card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Date & Time */}
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-sm font-medium text-textSecondary mb-3">
            {t('appointmentDetail.dateTime', 'Date & Time')}
          </h3>
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-primary-500" />
            <span className="text-textPrimary font-medium capitalize">{dateFormatted}</span>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-primary-500" />
            <span className="text-textPrimary">{timeFormatted}</span>
          </div>
        </div>

        {appointment.checkInCode && (
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-sm font-medium text-textSecondary mb-3">
              {t('appointmentDetail.checkInCode', "Code d'accueil")}
            </h3>
            <p className="text-2xl font-semibold tracking-[0.35em] text-textPrimary">
              {appointment.checkInCode}
            </p>
            <p className="text-sm text-textSecondary mt-2">
              {t(
                'appointmentDetail.checkInCodeHint',
                'Donnez ce code à l’accueil si le scan QR n’est pas possible',
              )}
            </p>
          </div>
        )}

        {/* Location */}
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-sm font-medium text-textSecondary mb-3">
            {t('appointmentDetail.location', 'Location')}
          </h3>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary-500 mt-0.5" />
            <div>
              <p className="text-textPrimary font-medium">{appointment.shopName}</p>
              {shopAddressStr && <p className="text-sm text-textSecondary">{shopAddressStr}</p>}
              {appointment.shopPhone && (
                <p className="text-sm text-textSecondary mt-1">{appointment.shopPhone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Practitioner */}
        {appointment.practitionerName && (
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-sm font-medium text-textSecondary mb-3">
              {t('appointmentDetail.practitioner', 'Practitioner')}
            </h3>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-primary-500" />
              <span className="text-textPrimary">{appointment.practitionerName}</span>
            </div>
          </div>
        )}

        {/* Tattoo project details */}
        {appointment.project && (
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-sm font-medium text-textSecondary mb-3">
              {t('appointmentDetail.project', 'Project Details')}
            </h3>
            <div className="space-y-2 text-sm">
              {appointment.project.style && (
                <div className="flex justify-between">
                  <span className="text-textSecondary">{t('appointmentDetail.style', 'Style')}</span>
                  <span className="text-textPrimary font-medium">{appointment.project.style}</span>
                </div>
              )}
              {appointment.project.placement && (
                <div className="flex justify-between">
                  <span className="text-textSecondary">{t('appointmentDetail.placement', 'Placement')}</span>
                  <span className="text-textPrimary">{appointment.project.placement}</span>
                </div>
              )}
              {appointment.project.size && (
                <div className="flex justify-between">
                  <span className="text-textSecondary">{t('appointmentDetail.size', 'Size')}</span>
                  <span className="text-textPrimary">{appointment.project.size}</span>
                </div>
              )}
              {appointment.project.colorType && (
                <div className="flex justify-between">
                  <span className="text-textSecondary">{t('appointmentDetail.color', 'Color')}</span>
                  <span className="text-textPrimary">{appointment.project.colorType}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reference Images */}
        {appointment.referenceImages && appointment.referenceImages.length > 0 && (
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-sm font-medium text-textSecondary mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              {t('appointmentDetail.referenceImages', 'Reference Images')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {appointment.referenceImages.map((img) => (
                <a
                  key={img.id}
                  href={img.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group rounded-lg overflow-hidden border border-gray-200 hover:border-primary-400 transition-colors"
                >
                  <img
                    src={img.thumbnailUrl || img.url}
                    alt={img.fileName}
                    className="w-full h-28 object-cover group-hover:opacity-90 transition-opacity"
                  />
                  <p className="text-xs text-textSecondary truncate px-2 py-1">{img.fileName}</p>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Pricing */}
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-sm font-medium text-textSecondary mb-3">
            {t('appointmentDetail.pricing', 'Pricing')}
          </h3>
          <div className="space-y-2 text-sm">
            {isConsultation ? (
              <div className="flex justify-between">
                <span className="text-textSecondary">{t('appointmentDetail.total', 'Total')}</span>
                <span className="text-textPrimary font-semibold">{t('onlineBooking.service.free', 'Free')}</span>
              </div>
            ) : !hideTattooTotal ? (
              <div className="flex justify-between">
                <span className="text-textSecondary">{t('appointmentDetail.total', 'Total')}</span>
                <span className="text-textPrimary font-semibold">{totalPrice} &euro;</span>
              </div>
            ) : null}
            {appointment.depositRequired && (
              <>
                <div className="flex justify-between">
                  <span className="text-textSecondary">{t('appointmentDetail.deposit', 'Deposit')}</span>
                  <span className="text-textPrimary">{appointment.depositAmount ?? 0} &euro;</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-textSecondary">{t('appointmentDetail.depositStatusLabel', 'Deposit status')}</span>
                  <span className="text-textPrimary font-medium">
                    {depositStatusLabels[appointment.depositStatus || ''] || '—'}
                  </span>
                </div>
              </>
            )}
            {isTattooSession && (
              <p className="text-xs text-textSecondary pt-1">
                {t('appointmentDetail.quoteNote', 'The final price will be determined based on a quote provided on the day of the appointment.')}
              </p>
            )}
          </div>
        </div>

        {/* Notes */}
        {appointment.notes && (
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-sm font-medium text-textSecondary mb-3">
              <Scissors className="w-4 h-4 inline mr-1" />
              {t('appointmentDetail.notes', 'Notes')}
            </h3>
            <p className="text-sm text-textPrimary whitespace-pre-line">{appointment.notes}</p>
          </div>
        )}

        {/* Client Notes (editable) */}
        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-textSecondary">
              {t('appointmentDetail.clientNotes', 'Your notes to the practitioner')}
            </h3>
            {!editingNotes && isEditable && (
              <button
                onClick={() => {
                  setNoteDraft(clientNotesText);
                  setEditingNotes(true);
                }}
                className="text-xs fo-link flex items-center gap-1"
              >
                <Edit3 className="w-3 h-3" />
                {t('appointmentDetail.editNotes', 'Edit')}
              </button>
            )}
          </div>
          {editingNotes ? (
            <div className="space-y-3">
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('appointmentDetail.notesPlaceholder', 'Add a note for your practitioner...')}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    if (!appointment) return;
                    setNoteSaving(true);
                    try {
                      const kind = appointment.type?.toUpperCase().includes('TATTOO') ? 'tattoo' as const : 'piercing' as const;
                      const notePayload = { kind, noteHtml: `<p>${noteDraft}</p>` };
                      await clientAppointmentsApi.updateNotes(appointment.id, notePayload);
                      const storedNotes = JSON.stringify({ kind, version: 1, noteHtml: `<p>${noteDraft}</p>` });
                      setAppointment(prev => prev ? { ...prev, clientNotes: storedNotes } : prev);
                      setEditingNotes(false);
                    } catch {
                      // ignore
                    } finally {
                      setNoteSaving(false);
                    }
                  }}
                  disabled={noteSaving}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <Save className="w-3 h-3" />
                  {noteSaving ? t('appointmentDetail.saving', 'Saving...') : t('appointmentDetail.save', 'Save')}
                </button>
                <button
                  onClick={() => setEditingNotes(false)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : clientNotesText ? (
            <p className="text-sm text-textPrimary whitespace-pre-line bg-blue-50 p-3 rounded-lg">{clientNotesText}</p>
          ) : (
            <p className="text-sm text-textSecondary italic">{t('appointmentDetail.noClientNotes', 'No notes added yet.')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
